import { QueryClient } from "@tanstack/react-query";

// Create a client
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1, // Only retry once
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      queryFn: async ({ queryKey }: { queryKey: readonly unknown[] }): Promise<any> => {
        return await fetchAPI(queryKey[0] as string, "GET");
      },
    },
  },
});

type RequestMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

interface ApiRequestOptions {
  on401?: "returnNull" | "throw";
}

export const getQueryFn = (options: ApiRequestOptions = {}) => {
  return async ({ queryKey }: { queryKey: string[] }): Promise<any> => {
    return await fetchAPI(queryKey[0], "GET", undefined, options);
  };
};

export async function apiRequest(
  method: RequestMethod,
  endpoint: string,
  data?: any,
  options: ApiRequestOptions = {}
): Promise<any> {
  const url = endpoint.startsWith("http") ? endpoint : endpoint;

  const fetchOptions: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include", // Include cookies in requests
  };

  if (data !== undefined) {
    fetchOptions.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(url, fetchOptions);

    if (response.status === 401 && options.on401 === "returnNull") {
      return null as any;
    }

    if (!response.ok) {
      const errorData = await response.text();
      let errorMessage;
      try {
        // Try to parse as JSON first
        const errorJson = JSON.parse(errorData);
        errorMessage = errorJson.error || errorData;
      } catch {
        // If not JSON, use text directly
        errorMessage = errorData || `Request failed with status ${response.status}`;
      }
      console.error(`API error response:`, errorMessage);
      throw new Error(errorMessage);
    }

    // Return the parsed JSON data, not the Response object
    return await response.json();
  } catch (error) {
    console.error(`API ${method} request to ${endpoint} failed:`, error);
    throw error;
  }
}

export async function fetchAPI(
  endpoint: string,
  method: RequestMethod = "GET",
  data?: any,
  options: ApiRequestOptions = {}
): Promise<any> {
  // apiRequest now returns parsed JSON directly
  return await apiRequest(method, endpoint, data, options);
}